import React from 'react';

import { ActivityCard } from '@adsk/alloy-react-activity-log';
import { Flex, Box } from '@adsk/alloy-react-box';
import { useLocale } from '@adsk/alloy-react-locale';

import { ACTIVITY_VARIANTS } from '../../../../constants';
import { formatPath } from '../../../helpers';
import { ActivityType, ScheduleActivityEntity } from '../../types';

type ScheduleActivityCardProps = {
  variant?: typeof ACTIVITY_VARIANTS.ADDED | typeof ACTIVITY_VARIANTS.REMOVED;
  entity?: { id: string; activity: ScheduleActivityEntity };
  date: Date | string | number;
  user?: { name: string };
};

const ScheduleActivityCardDetails = ({
  variant,
  activity,
}: {
  variant?: typeof ACTIVITY_VARIANTS.ADDED | typeof ACTIVITY_VARIANTS.REMOVED;
  activity: ScheduleActivityEntity;
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const {
    sequentialId,
    name,
    isCriticalPath,
    completionPercentage: _completionPercentage,
    wbsPathActivities,
    type,
    scheduleName,
  } = activity;

  // Original order is parent > grandparent > great grandparent > ... > top ancestor
  const path =
    wbsPathActivities?.map(({ name }) => name ?? 'Untitled Activity') ?? [];
  const formattedPath = formatPath(path);

  return (
    <Flex flexDirection="column" position="relative" color="charcoal900">
      <Flex alignItems="center">
        {type === ActivityType.Milestone && (
          <Box
            backgroundColor={isCriticalPath ? 'red300' : 'charcoal300'}
            borderRadius="2px"
            width="11px"
            height="11px"
            marginRight="8px"
            style={{ transform: 'rotate(45deg)' }}
          />
        )}
        <Flex
          marginRight="8px"
          maxWidth="300px"
          {...(variant === ACTIVITY_VARIANTS.REMOVED && {
            color: 'charcoal700',
            typography: 'bodyMedium',
            style: {
              textDecorationLine: 'line-through',
            },
          })}
          {...(variant === ACTIVITY_VARIANTS.ADDED && {
            typography: 'bodyMediumBold',
          })}
        >{`#${sequentialId} - ${name}`}</Flex>
        <Box typography="bodySmall" color="red700">
          {isCriticalPath ? T.translate('CRITICAL') : ''}
        </Box>
      </Flex>
      <Box typography="bodySmall" color="charcoal800" marginTop="8px">
        {scheduleName}
      </Box>
      <Box typography="caption" color="charcoal500">
        {path.length > 0 && `└ ${formattedPath}`}
      </Box>
    </Flex>
  );
};

const ScheduleActivityCard = ({
  entity,
  variant,
  ...rest
}: ScheduleActivityCardProps) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  return (
    <ActivityCard
      {...rest}
      title={
        variant === ACTIVITY_VARIANTS.REMOVED
          ? T.translate('SCHEDULE_ACTIVITY_TITLE_REMOVED')
          : T.translate('SCHEDULE_ACTIVITY_TITLE_ADDED')
      }
    >
      {entity && (
        <ScheduleActivityCardDetails
          activity={entity.activity}
          variant={variant}
        />
      )}
    </ActivityCard>
  );
};

export default ScheduleActivityCard;
